import { FLAGS } from '@gozoki/feature-flags'
import { FORECAST_REFERENCE_QUERY_OPTIONS } from '~/lib/forecast-reference-queries'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTabs, type AppTab } from '~/components/new/app-ui/app-tabs'
import { Title } from '~/components/new/app-ui/title'
import { SandboxBackButton } from '~/components/routes/sandbox-previsions/sandbox-back-button'
import { TabsContent } from '~/components/ui/tabs'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'
import { api } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'
import { formatFrDate } from '../previsions-produits/-short-term-data'
import { PrevisionsStoreSelector } from '../previsions-long-terme/-previsions-store-selector'
import { CorrectionsToggle } from '../previsions-produits/-corrections-toggle'
import { WeekdaysToggle } from '../previsions-produits/-weekdays-toggle'
import { SegmentToggle } from '../previsions/-segment-toggle'
import { CategoryBacktest } from './-category-backtest'
import { CategoryFilter } from './-category-filter'
import { CategoryTable } from './-category-table'
import {
  effectiveAnchor,
  useCategoryBacktest,
  useCategoryForecast,
  type CategoryCell,
  type CategoryDailyPoint,
} from './-category-data'
import { DailyChartModal } from './-daily-chart-modal'
import { ExplainModal } from './-explain-modal'
import { GuideButton } from './-guide-button'
import { CATEGORY_KINDS, categorySearchSchema } from './-search-schema'

const TABS: AppTab<'forecast' | 'backtest'>[] = [
  { value: 'forecast', label: 'Prévision' },
  { value: 'backtest', label: 'Backtest' },
]

export const Route = createFileRoute('/_authenticated/previsions-categories/')({
  validateSearch: categorySearchSchema,
  staticData: { permission: ['read', 'previsions.previsions.forecast'] },
  component: CategoryPrevisionPage,
})

withRoutePermissions(Route)

function CategoryPrevisionPage() {
  const enabled = useFeatureFlag(FLAGS.categoryForecasting31days)
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [dailyCell, setDailyCell] = useState<{ label: string; daily: CategoryDailyPoint[] } | null>(
    null,
  )
  const [explained, setExplained] = useState<{
    storeId: number
    storeLabel: string
    categoryId: number
    categoryName: string
  } | null>(null)

  const setSearch = (next: Partial<typeof search>) =>
    navigate({ search: (prev) => ({ ...prev, ...next }) })

  const { data: stores = [] } = useQuery(
    api.forecast.forecastStores.queryOptions({}, FORECAST_REFERENCE_QUERY_OPTIONS),
  )
  const { data: categories = [] } = useQuery(
    api.forecast.productCategories.queryOptions(
      {},
      { enabled, ...FORECAST_REFERENCE_QUERY_OPTIONS },
    ),
  )
  const { data: segmentCategories } = useQuery(
    api.forecast.segmentCategories.queryOptions(
      {},
      { enabled, ...FORECAST_REFERENCE_QUERY_OPTIONS },
    ),
  )
  const forecast = useCategoryForecast(search, enabled)
  const backtest = useCategoryBacktest(search, enabled)

  const categoriesForKind = (): { id: number; name: string }[] => {
    if (search.kind === 'industriel') return segmentCategories?.internal ?? []
    if (search.kind === 'reappro') return segmentCategories?.reappro ?? []
    return categories
  }
  const kindCategories = categoriesForKind()

  const storeLabels = new Map(stores.map((store) => [store.storeId, store.label]))
  const categoryNames = new Map(kindCategories.map((category) => [category.id, category.name]))
  const storeLabelOf = (id: number) => storeLabels.get(id) ?? `Boutique ${id}`
  const categoryNameOf = (id: number) => categoryNames.get(id) ?? `Catégorie ${id}`

  const onOpenDaily = (storeId: number, cell: CategoryCell) =>
    setDailyCell({
      label: `${storeLabelOf(storeId)} — ${categoryNameOf(cell.categoryId)}`,
      daily: cell.daily,
    })
  const onExplain = (storeId: number, categoryId: number) =>
    setExplained({
      storeId,
      storeLabel: storeLabelOf(storeId),
      categoryId,
      categoryName: categoryNameOf(categoryId),
    })

  if (!enabled) {
    return (
      <>
        <AppHeader>
          <div className="flex items-center gap-3">
            <SandboxBackButton />
            <Title>Prévisions par catégorie</Title>
          </div>
        </AppHeader>
        <AppContainer>
          <p className="text-muted-foreground">Fonctionnalité non activée.</p>
        </AppContainer>
      </>
    )
  }

  return (
    <>
      <AppHeader>
        <div className="flex items-center gap-3">
          <SandboxBackButton />
          <Title>Prévisions par catégorie</Title>
          <GuideButton />
        </div>
      </AppHeader>
      <AppContainer>
        <div className="flex flex-wrap items-center gap-3">
          <SegmentToggle
            value={search.kind}
            segments={CATEGORY_KINDS}
            onChange={(kind) => setSearch({ kind, categoryIds: [] })}
          />

          <PrevisionsStoreSelector
            selectedIds={search.storeIds}
            onApply={(storeIds) => setSearch({ storeIds })}
            placeholder="Sélectionner des boutiques"
            title="Boutiques à prévoir"
          />

          <CategoryFilter
            categories={kindCategories}
            selectedIds={search.categoryIds}
            onApply={(categoryIds) => setSearch({ categoryIds })}
          />

          <CorrectionsToggle
            checked={search.corrections}
            onChange={(corrections) => setSearch({ corrections })}
          />

          <WeekdaysToggle
            checked={search.weekdaysOnly}
            onChange={(weekdaysOnly) => setSearch({ weekdaysOnly })}
          />
        </div>

        <AppTabs tabs={TABS} value={search.tab} onTabChange={(tab) => setSearch({ tab })}>
          <TabsContent value="forecast">
            {search.storeIds.length === 0 && (
              <p className="text-muted-foreground">Sélectionnez une ou plusieurs boutiques.</p>
            )}
            {search.storeIds.length > 0 && forecast.isError && (
              <p className="text-amber-600">Erreur lors du calcul de la prévision. Réessayez.</p>
            )}
            {search.storeIds.length > 0 && forecast.isPending && (
              <p className="text-muted-foreground">Calcul de la prévision…</p>
            )}
            {forecast.data && (
              <>
                <p className="mb-4 text-sm text-muted-foreground">
                  Prévision des {forecast.data.days} prochains jours (ancre :{' '}
                  <strong className="text-foreground">{formatFrDate(forecast.data.anchor)}</strong>
                  ).
                </p>
                {forecast.data.warnings.length > 0 && (
                  <div className="mb-4 flex flex-col gap-1 text-sm text-amber-600">
                    {forecast.data.warnings.map((warning) => (
                      <p key={warning}>{warning}</p>
                    ))}
                  </div>
                )}
                <CategoryTable
                  stores={forecast.data.stores}
                  storeLabels={storeLabels}
                  categoryNames={categoryNames}
                  onOpenDaily={onOpenDaily}
                  onExplain={onExplain}
                />
              </>
            )}
          </TabsContent>

          <TabsContent value="backtest">
            {search.storeIds.length === 0 && (
              <p className="text-muted-foreground">Sélectionnez une ou plusieurs boutiques.</p>
            )}
            {search.storeIds.length > 0 && (
              <CategoryBacktest
                anchor={effectiveAnchor(search.anchor, search.days)}
                onAnchorChange={(anchor) => setSearch({ anchor })}
                data={backtest.data}
                storeLabels={storeLabels}
                categoryNames={categoryNames}
                formatDate={formatFrDate}
                days={search.days}
                isPending={backtest.isPending}
                isError={backtest.isError}
                correctionsOn={search.corrections}
              />
            )}
          </TabsContent>
        </AppTabs>

        {dailyCell && (
          <DailyChartModal
            label={dailyCell.label}
            daily={dailyCell.daily}
            onClose={() => setDailyCell(null)}
          />
        )}

        {explained && forecast.data && (
          <ExplainModal
            kind={search.kind}
            storeId={explained.storeId}
            storeLabel={explained.storeLabel}
            categoryId={explained.categoryId}
            categoryName={explained.categoryName}
            anchor={forecast.data.anchor}
            days={forecast.data.days}
            corrections={search.corrections}
            vmjDays={search.vmjDays}
            weekdaysOnly={search.weekdaysOnly}
            onClose={() => setExplained(null)}
          />
        )}
      </AppContainer>
    </>
  )
}
